import { ArrowLeft, ArrowRight, CheckCircle, Lightbulb, NotePencil, UserFocus } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { NiveauBadge, libelleProgression, type HgNiveauMaitrise } from './hg-college-qcm-vue';

/* ── Types (miroir de packages/application/src/trpc/rows/hg-college.ts) ── */

export interface HgRedactionCritereRow {
  id: string;
  code: string;
  libelle: string;
}

export interface HgRedactionExerciceRow {
  id: string;
  fiche_id: string;
  fiche_code: string;
  fiche_titre: string;
  famille: 'redaction_argumentee';
  position: number;
  enonce: string;
  consigne: string;
  mots_inducteurs: string[];
  longueur_attendue: { min_lignes: number; cible_lignes: number };
  duree_indicative_s: number | null;
  criteres: HgRedactionCritereRow[];
}

export interface HgRedactionRetourCritereRow {
  critere_id: string;
  code: string;
  libelle: string;
  niveau_atteint: HgNiveauMaitrise;
}

export interface HgRedactionTentativeResultRow {
  tentative_id: string;
  tentative_n: number;
  niveau_global: HgNiveauMaitrise;
  origine_globale: 'declaratif';
  criteres: HgRedactionRetourCritereRow[];
}

/* Descripteurs des 4 critères nationaux du développement construit (échelle
   descriptive, sujets DNB 2026 série professionnelle), à la première
   personne. Ils sont AFFICHÉS pour que l'élève se situe ; aucune ligne de
   code ne les attribue — miroir de DESCRIPTEURS_JUSTIFICATION_ELEVE
   (hg-college-analyse-doc-vue.tsx), étendu à 4 critères distincts. */
export const DESCRIPTEURS_REDACTION_ELEVE: Record<string, Record<HgNiveauMaitrise, string>> = {
  'redac-respect-consigne': {
    1: 'Je ne réponds pas à ce qui est demandé.',
    2: 'Je me contente de raconter, sans répondre précisément à la consigne.',
    3: 'Mon texte répond au sujet et évoque au moins une caractéristique attendue.',
    4: 'Mon texte répond au sujet et évoque au moins deux caractéristiques attendues.',
  },
  'redac-organisation': {
    1: "Je n'ai pas organisé mes idées.",
    2: "Je n'ai développé qu'une seule idée.",
    3: 'J’ai développé plusieurs idées (au moins deux), organisées avec des mots-clés.',
    4: 'Mes idées sont développées et articulées explicitement entre elles, pas seulement juxtaposées.',
  },
  'redac-connaissances-exemples': {
    1: "Je n'ai pas utilisé de connaissances précises.",
    2: 'Mes connaissances sont vagues ou mes exemples ne sont pas liés au sujet.',
    3: 'Mes idées reposent sur des connaissances précises et des exemples liés au sujet.',
    4: 'Mes connaissances sont précises et mes exemples nombreux et bien choisis.',
  },
  'redac-langue': {
    1: 'Ma rédaction ne peut pas être comprise.',
    2: 'Ma rédaction peut être comprise partiellement.',
    3: 'Mon orthographe est soignée et mes phrases sont complètes.',
    4: 'Ma langue est correcte sans réserve.',
  },
};

export const NIVEAUX_AUTO: readonly HgNiveauMaitrise[] = [1, 2, 3, 4];

/* Estimation, jamais une mesure : un compte de lignes réel dépend d'une
   largeur de copie manuscrite que le navigateur ne connaît pas. ~70
   caractères par ligne est l'ordre de grandeur d'une ligne de copie
   d'examen — affiché comme une estimation, jamais comme le compte officiel. */
const CARACTERES_PAR_LIGNE_ESTIMEE = 70;

export function estimerLignes(texte: string): number {
  if (texte.trim().length === 0) return 0;
  const parSaut = texte.split('\n').length;
  const parLongueur = Math.ceil(texte.length / CARACTERES_PAR_LIGNE_ESTIMEE);
  return Math.max(parSaut, parLongueur);
}

export interface HgRedactionExerciceVueProps {
  exercice: HgRedactionExerciceRow;
  index: number;
  total: number;
  seriesFaites?: readonly boolean[];
  texte: string;
  autoPositionnements: Record<string, HgNiveauMaitrise | undefined>;
  result?: HgRedactionTentativeResultRow;
  submitting?: boolean;
  submitError?: string | null;
  onEcrire: (texte: string) => void;
  onAutoPositionner: (critereId: string, niveau: HgNiveauMaitrise) => void;
  onPrecedent: () => void;
  onSuivant: () => void;
  onValider: () => void;
}

export function HgRedactionExerciceVue({
  exercice,
  index,
  total,
  seriesFaites,
  texte,
  autoPositionnements,
  result,
  submitting,
  submitError,
  onEcrire,
  onAutoPositionner,
  onPrecedent,
  onSuivant,
  onValider,
}: HgRedactionExerciceVueProps) {
  const lignesEstimees = estimerLignes(texte);
  const tousPositionnes = exercice.criteres.every((critere) => autoPositionnements[critere.id] !== undefined);
  const pretAValider = texte.trim().length > 0 && tousPositionnes;

  return (
    <div>
      <div className="flex items-start gap-[10px] mb-2">
        <NotePencil size={24} weight="light" className="text-primary shrink-0 mt-[2px]" />
        <h2 className="text-[19px] font-bold text-foreground leading-[1.3]">{exercice.fiche_titre}</h2>
      </div>

      {seriesFaites && seriesFaites.length > 1 && (
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <div className="flex gap-[5px]" aria-hidden="true">
            {seriesFaites.map((faite, rang) => (
              <span
                key={rang}
                className={cn(
                  'h-[6px] w-9 rounded-full',
                  faite ? 'bg-naia-blue' : rang === index ? 'bg-naia-sky' : 'bg-border',
                )}
              />
            ))}
          </div>
          <span className="text-[14px] text-muted-foreground">{libelleProgression(seriesFaites)}</span>
        </div>
      )}

      <div className="text-[14px] text-muted-foreground mb-6">
        Série {index + 1} / {total} — développement construit
      </div>

      <section className="mb-5">
        <p className="text-[18px] font-bold text-foreground leading-[1.4] mb-3">{exercice.consigne}</p>

        {exercice.mots_inducteurs.length > 0 && (
          <p className="flex items-start gap-2 text-[13px] text-muted-foreground leading-[1.6] m-0">
            <Lightbulb size={16} weight="light" className="shrink-0 mt-[2px]" />
            <span>
              Pour vous aider, vous pouvez utiliser certains des mots suivants :{' '}
              {exercice.mots_inducteurs.join(', ')}. Ce n’est jamais une obligation.
            </span>
          </p>
        )}
      </section>

      <section className="mb-7">
        <label htmlFor="redaction-texte" className="sr-only">
          Ton développement construit
        </label>
        <textarea
          id="redaction-texte"
          rows={16}
          maxLength={6000}
          disabled={Boolean(result)}
          value={texte}
          onChange={(event) => onEcrire(event.target.value)}
          className="w-full rounded-card border border-border bg-card p-4 text-[15px] leading-[1.6] text-foreground resize-y disabled:opacity-70"
          placeholder="Écris ton développement construit ici."
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-[13px] text-muted-foreground m-0">
            Ce texte n’est pas corrigé par l’application : il est enregistré tel quel et ton
            professeur peut le lire.
          </p>
          <p className="text-[13px] text-muted-foreground m-0 whitespace-nowrap ml-3">
            ~{lignesEstimees} ligne{lignesEstimees > 1 ? 's' : ''} (cible {exercice.longueur_attendue.min_lignes}+)
          </p>
        </div>
      </section>

      {!result && (
        <section className="mb-7 flex flex-col gap-4">
          {exercice.criteres.map((critere) => {
            const descripteurs = DESCRIPTEURS_REDACTION_ELEVE[critere.code];
            const positionnement = autoPositionnements[critere.id];
            return (
              <div key={critere.id} className="rounded-card border border-border bg-card p-5">
                <p className="flex items-center gap-[10px] m-0 mb-1 text-[16px] font-bold text-foreground">
                  <UserFocus size={22} weight="light" className="text-primary shrink-0" />
                  {critere.libelle}
                </p>
                <p className="m-0 mb-4 text-[14px] text-muted-foreground leading-[1.6]">
                  C’est toi qui te situes ici. L’application ne lit pas ton texte.
                </p>
                <div className="flex flex-col gap-[10px]">
                  {NIVEAUX_AUTO.map((niveau) => {
                    const actif = positionnement === niveau;
                    return (
                      <button
                        key={niveau}
                        type="button"
                        onClick={() => onAutoPositionner(critere.id, niveau)}
                        className={cn(
                          'w-full text-left px-4 py-[13px] rounded-card border text-[15px] min-h-[44px] transition-shadow cursor-pointer',
                          actif
                            ? 'border-primary bg-primary text-primary-foreground font-bold'
                            : 'border-border bg-background text-foreground hover:shadow-card',
                        )}
                      >
                        {descripteurs?.[niveau] ?? `Niveau ${niveau}/4`}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {result && <RetourRedaction result={result} />}

      {submitError && <p role="alert" className="text-destructive text-sm mt-4">{submitError}</p>}

      <div className="flex gap-3 mt-7">
        <button
          type="button"
          disabled={index === 0}
          onClick={onPrecedent}
          className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card border border-border bg-card text-foreground font-bold text-[15px] cursor-pointer min-h-[44px] transition-shadow hover:shadow-card disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
        >
          <ArrowLeft size={18} />
          Précédent
        </button>

        {!result && (
          <button
            type="button"
            disabled={!pretAValider || submitting}
            onClick={onValider}
            className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card bg-primary text-primary-foreground font-bold text-[15px] border-none cursor-pointer min-h-[44px] transition-shadow hover:shadow-card disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none"
          >
            <CheckCircle size={18} weight="bold" />
            {submitting ? 'Envoi…' : 'Valider'}
          </button>
        )}

        {result && index < total - 1 && (
          <button
            type="button"
            onClick={onSuivant}
            className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card bg-primary text-primary-foreground font-bold text-[15px] border-none cursor-pointer min-h-[44px] transition-shadow hover:shadow-card"
          >
            <ArrowRight size={18} />
            Suivant
          </button>
        )}
      </div>
    </div>
  );
}

export function RetourRedaction({ result }: { result: HgRedactionTentativeResultRow }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="rounded-card border border-border bg-accent p-4">
        <p className="flex items-center gap-2 m-0 text-[13px] text-muted-foreground">
          <UserFocus size={16} weight="light" className="shrink-0" />
          Niveau global {result.niveau_global}/4 — c’est la moyenne de ce que tu t’es donné,
          pas une mesure de l’application.
        </p>
      </div>

      {result.criteres.map((critere) => (
        <article key={critere.critere_id} className="rounded-card border border-border bg-card p-5">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <NiveauBadge niveau={critere.niveau_atteint} />
            <span className="text-[16px] font-bold text-foreground">{critere.libelle}</span>
          </div>
          <p className="flex items-center gap-2 text-[13px] text-muted-foreground m-0">
            <UserFocus size={16} weight="light" className="shrink-0" />
            Niveau que tu t’es donné. L’application n’a pas lu ton texte.
          </p>
        </article>
      ))}
    </section>
  );
}
